import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

class ChatReminderService {
  constructor() {
    this.REMINDER_KEY = 'last_chat_session';
    this.NOTIFICATION_IDS = {
      day2: 201,  // 2 days after
      day5: 202,  // 5 days after
      day7: 203   // 7 days after
    };
  }

  async initialize() {
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Running on web - notifications require native platform');
      return false;
    }
    
    try {
      const permission = await LocalNotifications.requestPermissions();
      if (permission.display === 'granted') {
        console.log('✅ Chat reminder permissions granted');
        return true;
      } else {
        console.log('⚠️ Chat reminder permissions denied');
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return false;
    }
  }

  getTimeOfDay() {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  }

  getReminderContent(userName, daysAgo, timeOfDay) {
    const reminders = {
      2: {
        morning: [
          {
            title: `☀️ Good Morning, ${userName}`,
            body: `It's been a couple days. How's your morning going?`
          },
          {
            title: `🌅 Hey ${userName}`,
            body: `Morning! Catch up with Preptune when you have a moment.`
          },
          {
            title: `✨ Morning, ${userName}`,
            body: `It's been 2 days. Want to chat about what's new?`
          }
        ],
        afternoon: [
          {
            title: `🌤️ Hey ${userName}`,
            body: `Good afternoon! It's been a couple days. How are things?`
          },
          {
            title: `☕ Hi ${userName}`,
            body: `Afternoon! Want to catch up with Preptune?`
          },
          {
            title: `⏸️ Hey ${userName}`,
            body: `It's been 2 days. How's your day going so far?`
          }
        ],
        evening: [
          {
            title: `🌙 Evening, ${userName}`,
            body: `It's been a couple days. How's your evening going?`
          },
          {
            title: `🌆 Hey ${userName}`,
            body: `Good evening! Want to chat about your day?`
          },
          {
            title: `✨ Hi ${userName}`,
            body: `It's been 2 days. Catch up with Preptune tonight?`
          }
        ]
      },
      5: {
        morning: [
          {
            title: `🌅 Missing You, ${userName}`,
            body: `It's been almost a week. How have you been?`
          },
          {
            title: `☀️ Hey ${userName}`,
            body: `Good morning! It's been 5 days. Want to catch up?`
          },
          {
            title: `✨ ${userName}, It's Been a While`,
            body: `Morning! Let's chat about what's been going on.`
          }
        ],
        afternoon: [
          {
            title: `🌤️ ${userName}, It's Been a While`,
            body: `5 days! How's your afternoon? Want to catch up?`
          },
          {
            title: `☕ Missing Our Chats, ${userName}`,
            body: `It's been almost a week. How are things?`
          },
          {
            title: `💬 Hey ${userName}`,
            body: `It's been 5 days. Let's catch up this afternoon.`
          }
        ],
        evening: [
          {
            title: `🌙 ${userName}, It's Been a While`,
            body: `5 days! How's your evening? Want to talk?`
          },
          {
            title: `🌆 Missing You, ${userName}`,
            body: `It's been almost a week. How have things been?`
          },
          {
            title: `✨ Hey ${userName}`,
            body: `5 days without chatting. Want to catch up tonight?`
          }
        ]
      },
      7: {
        morning: [
          {
            title: `🌅 ${userName}, It's Been a Week`,
            body: `Good morning! A lot can happen in a week. Want to share?`
          },
          {
            title: `☀️ Miss Chatting, ${userName}`,
            body: `It's been 7 days. How's your morning? Let's catch up.`
          },
          {
            title: `✨ Hey ${userName}`,
            body: `A whole week! Want to tell Preptune what's new?`
          }
        ],
        afternoon: [
          {
            title: `🌤️ ${userName}, It's Been a Week`,
            body: `7 days! How's your afternoon? Preptune is here when you're ready.`
          },
          {
            title: `☕ Miss Our Chats, ${userName}`,
            body: `It's been a whole week. How have you been?`
          },
          {
            title: `💬 Hey ${userName}`,
            body: `A week without chatting! Want to catch up this afternoon?`
          }
        ],
        evening: [
          {
            title: `🌙 ${userName}, It's Been a Week`,
            body: `Good evening! 7 days is a long time. How's everything going?`
          },
          {
            title: `🌆 Miss You, ${userName}`,
            body: `It's been a whole week. Want to chat about your week?`
          },
          {
            title: `✨ Hey ${userName}`,
            body: `A week already! How's your evening? Let's catch up.`
          }
        ]
      }
    };

    const dayReminders = reminders[daysAgo] || reminders[7];
    const timeReminders = dayReminders[timeOfDay] || dayReminders.evening;
    return timeReminders[Math.floor(Math.random() * timeReminders.length)];
  }

  async scheduleReminderAfterChat(userName) {
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Notifications only work on mobile devices');
      return false;
    }

    if (!userName || userName.trim() === '') {
      userName = 'there';
    }

    try {
      console.log(`🔔 Scheduling chat reminders for ${userName}...`);
      
      // Cancel any existing chat reminders
      await LocalNotifications.cancel({ 
        notifications: [
          { id: this.NOTIFICATION_IDS.day2 },
          { id: this.NOTIFICATION_IDS.day5 },
          { id: this.NOTIFICATION_IDS.day7 }
        ]
      });

      const notifications = [];
      const now = new Date();

      // ===================================
      // 2 DAYS LATER REMINDER
      // ===================================
      const day2Time = new Date(now);
      day2Time.setDate(day2Time.getDate() + 2);
      
      // Set to preferred notification time
      const preferredHour = this.getPreferredNotificationHour();
      day2Time.setHours(preferredHour, 0, 0, 0);

      const day2TimeOfDay = this.getTimeOfDayForHour(preferredHour);
      const day2Content = this.getReminderContent(userName, 2, day2TimeOfDay);
      
      notifications.push({
        id: this.NOTIFICATION_IDS.day2,
        title: day2Content.title,
        body: day2Content.body,
        schedule: { 
          at: day2Time
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { 
          type: 'chat-reminder',
          daysAgo: 2,
          userName: userName
        }
      });

      console.log(`📅 Day 2 reminder: ${day2Time.toLocaleString()}`);

      // ===================================
      // 5 DAYS LATER REMINDER
      // ===================================
      const day5Time = new Date(now);
      day5Time.setDate(day5Time.getDate() + 5);
      day5Time.setHours(preferredHour, 0, 0, 0);

      const day5TimeOfDay = this.getTimeOfDayForHour(preferredHour);
      const day5Content = this.getReminderContent(userName, 5, day5TimeOfDay);
      
      notifications.push({
        id: this.NOTIFICATION_IDS.day5,
        title: day5Content.title,
        body: day5Content.body,
        schedule: { 
          at: day5Time
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { 
          type: 'chat-reminder',
          daysAgo: 5,
          userName: userName
        }
      });

      console.log(`📅 Day 5 reminder: ${day5Time.toLocaleString()}`);

      // ===================================
      // 7 DAYS LATER REMINDER
      // ===================================
      const day7Time = new Date(now);
      day7Time.setDate(day7Time.getDate() + 7);
      day7Time.setHours(preferredHour, 0, 0, 0);

      const day7TimeOfDay = this.getTimeOfDayForHour(preferredHour);
      const day7Content = this.getReminderContent(userName, 7, day7TimeOfDay);
      
      notifications.push({
        id: this.NOTIFICATION_IDS.day7,
        title: day7Content.title,
        body: day7Content.body,
        schedule: { 
          at: day7Time
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        iconColor: '#667eea',
        extra: { 
          type: 'chat-reminder',
          daysAgo: 7,
          userName: userName
        }
      });

      console.log(`📅 Day 7 reminder: ${day7Time.toLocaleString()}`);

      // ===================================
      // SCHEDULE ALL NOTIFICATIONS
      // ===================================
      await LocalNotifications.schedule({ notifications });
      
      console.log('✅ Chat reminders scheduled!');
      console.log(`   • User: ${userName}`);
      console.log('   • 3 reminders: Day 2, Day 5, Day 7');
      
      return true;

    } catch (error) {
      console.error('❌ Failed to schedule chat reminders:', error);
      console.error('   Error details:', error.message);
      return false;
    }
  }

  getPreferredNotificationHour() {
    // Get user's preferred time or default to 7 PM
    const storedHour = localStorage.getItem('preferred_notification_hour');
    return storedHour ? parseInt(storedHour) : 19; // Default: 7 PM
  }

  getTimeOfDayForHour(hour) {
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  }

  async cancelAllReminders() {
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 Running on web - no notifications to cancel');
      return;
    }
    
    try {
      await LocalNotifications.cancel({ 
        notifications: [
          { id: this.NOTIFICATION_IDS.day2 },
          { id: this.NOTIFICATION_IDS.day5 },
          { id: this.NOTIFICATION_IDS.day7 }
        ]
      });
      console.log('✅ All chat reminders cancelled');
    } catch (error) {
      console.error('❌ Failed to cancel chat reminders:', error);
    }
  }

  async rescheduleReminders(userName) {
    console.log('🔄 Rescheduling chat reminders...');
    await this.cancelAllReminders();
    await this.scheduleReminderAfterChat(userName);
  }
}

export default new ChatReminderService();